import React from "react"
import {
  Map,
  BarChart3,
  Leaf,
  ClipboardCheck,
  TreeDeciduous,
  Satellite,
  Bug,
  Settings
} from "lucide-react"
import { cn } from "../../lib/utils"
import { Button } from "../ui/button"
import { Sheet, SheetContent } from "../ui/sheet"

const navigationItems = [
  { id: 'charts', label: 'Dashboard', icon: BarChart3 },
  { id: 'map', label: 'Field Map', icon: Map },
  { id: 'satellite', label: 'Satellite View', icon: Satellite },
  { id: 'evaluation', label: 'Crop Analysis', icon: ClipboardCheck },
  { id: 'pest', label: 'Pest Risks', icon: Bug },
  { id: 'biodiversity', label: 'Biodiversity', icon: Leaf },
  { id: 'sustainability', label: 'Sustainability', icon: TreeDeciduous },
]


export function Sidebar({ activeView, onViewChange, isMobile = false, isOpen, onOpenChange }) {
  // const [open, setOpen] = useState(false) // Removed local state

  const handleViewChange = (viewId) => {
    onViewChange(viewId)
    if (isMobile && onOpenChange) {
      onOpenChange(false)
    }
  }

  const SidebarContent = () => (
    <nav className="flex flex-col space-y-1 p-2">
      {navigationItems.map((item) => {
        const Icon = item.icon
        const isActive = activeView === item.id
        return (
          <Button
            key={item.id}
            variant={isActive ? "secondary" : "ghost"}
            className={cn(
              "w-full justify-start relative",
              isActive && "bg-accent text-accent-foreground"
            )}
            onClick={() => handleViewChange(item.id)}
          >
            <Icon className="mr-2 h-4 w-4" />
            {item.label}
            {item.isNew && (
              <span className="ml-auto text-[10px] bg-primary text-primary-foreground px-1.5 py-0.5 rounded-full">
                NEW
              </span>
            )}
          </Button>
        )
      })}
    </nav>
  )

  if (isMobile) {
    return (
      <Sheet open={isOpen} onOpenChange={onOpenChange}>
        <SheetContent side="left" className="w-[300px] p-0">
          <SidebarContent />
        </SheetContent>
      </Sheet>
    )
  }

  return (
    <>
      <aside className="hidden md:flex fixed left-0 top-0 bottom-0 z-40 flex-col w-[70px] bg-card border-r border-border-strong">
        <div className="flex flex-col items-center py-6 gap-6 h-full">
          {/* Logo */}
          <div className="h-10 w-10 rounded-xl bg-primary/10 flex items-center justify-center text-primary mb-4">
            <Leaf className="h-6 w-6 fill-current" />
          </div>

          <nav className="flex flex-col gap-4 w-full px-3">
            {navigationItems.map((item) => {
              const Icon = item.icon
              const isActive = activeView === item.id
              return (
                <div key={item.id} className="relative group flex justify-center">
                  <Button
                    variant="ghost"
                    size="icon"
                    className={cn(
                      "w-10 h-10 rounded-xl transition-all duration-200",
                      isActive
                        ? "bg-primary text-primary-foreground shadow-[0_0_15px_rgba(46,204,113,0.3)]"
                        : "text-muted-foreground hover:bg-muted hover:text-foreground"
                    )}
                    onClick={() => handleViewChange(item.id)}
                  >
                    <Icon className="h-5 w-5" />
                  </Button>

                  {/* Tooltip */}
                  <div className="absolute left-14 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50">
                    <div className="bg-popover border border-border-strong text-popover-foreground px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap shadow-xl">
                      {item.label}
                    </div>
                  </div>
                </div>
              )
            })}
          </nav>

          {/* Bottom Settings Button */}
          <div className="mt-auto pb-4">
            <Button
              variant="ghost"
              size="icon"
              className="w-10 h-10 text-muted-foreground hover:text-foreground hover:bg-muted rounded-xl"
              onClick={() => onViewChange('settings')}
            >
              <Settings className="h-5 w-5" />
            </Button>
          </div>
        </div>
      </aside>

      {/* Spacer to push content */}
      <div className="hidden md:block w-24 flex-shrink-0" />
    </>
  )
}
